import { toast } from 'vue-sonner'
import { isHttpError } from '@/api/utils/http.ts'
import { DEEP_BOX_API_ERROR_MSGS } from '@/constants'
import { Sections, SECTIONS } from '@/types/sections'
import { MIME_TYPES } from '@deepcloud/deep-ui-utils'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info.ts'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useDeepBoxSearchStore } from '@/stores/deepbox/search.ts'
import { useApi } from '@/composables/api.ts'
import type {
  GenericError,
  NodeContent,
  PathSegment,
  SearchData,
} from '@deepcloud/api-deep-box'
import type { HttpClientResponse } from '@deepcloud/api-http-client'

export function useNodeMove() {
  const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
  const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
  const deepBoxSearchStore = useDeepBoxSearchStore()

  const { t } = useI18n()
  const { deepBoxAPI } = useApi()

  const moveNodePending = ref(false)

  async function moveNodes(payload: {
    section: Sections
    targetParentNode: PathSegment
    nodeIds: string[]
  }) {
    console.log('moveNodes', payload)
    moveNodePending.value = true
    try {
      const { targetParentNode, nodeIds, section } = payload
      const targetParentNodeId = targetParentNode.nodeId
      const params = { targetParentNodeId }

      const promises: Promise<HttpClientResponse<never>>[] = []

      let successMovedNodesCount = 0
      const handleApiResponse = (nodeId: string) => {
        // update current nodes state
        if (
          section &&
          (
            [
              SECTIONS.QUEUE,
              SECTIONS.FILES,
              SECTIONS.FILE_NODES,
              SECTIONS.TRASH,
              SECTIONS.TRASH_NODES,
            ] as Sections[]
          ).includes(section)
        ) {
          let dataSet
          let nodeIndex
          if (
            deepBoxDeepBoxesBoxesNodesStore.data &&
            deepBoxDeepBoxesBoxesNodesStore.data.nodes
          ) {
            dataSet = {
              ...deepBoxDeepBoxesBoxesNodesStore.data,
            } as NodeContent
            nodeIndex = dataSet.nodes.findIndex(
              (node) => node.nodeId === nodeId,
            )
            if (nodeIndex > -1) {
              dataSet.nodes.splice(nodeIndex, 1)
              dataSet.size -= 1
            }
          }

          if (targetParentNode.mimeType === MIME_TYPES.MIME_TYPE_INBOX) {
            // target is current box inbox
            if (
              deepBoxDeepBoxesBoxesNodesStore.rootNodes.queue?.nodeId ===
              targetParentNode.nodeId
            ) {
              if (deepBoxDeepBoxesBoxesNodesStore.data) {
                if (deepBoxDeepBoxesBoxesNodesStore.data.size) {
                  deepBoxDeepBoxesBoxesNodesStore.data.size += 1
                } else {
                  deepBoxDeepBoxesBoxesNodesStore.data.size = 1
                }
              }

              // update BoxInfo `queueCount` in case some files are moved to INBOX
              deepBoxBoxInfoStore.increaseBoxInfoQueueCounter(1)
            } else {
              if (section === SECTIONS.QUEUE) {
                deepBoxBoxInfoStore.decreaseBoxInfoQueueCounter(1)
              }
            }
          }

          if (dataSet) {
            deepBoxDeepBoxesBoxesNodesStore.data = dataSet
          }
        }

        // check if the moved node is within searchResults
        const dataSetSearchResults = {
          ...deepBoxSearchStore.searchResults,
        } as SearchData
        const index = dataSetSearchResults?.items?.findIndex(
          (item) => item.nodeId === nodeId,
        )
        if (index >= 0) {
          // if the node was already in trash, remove it from the list
          dataSetSearchResults.items[index].parentPath.segments = [
            targetParentNode,
          ]
          deepBoxSearchStore.searchResults = dataSetSearchResults
        }

        successMovedNodesCount += 1
      }

      type ErrorList = {
        e: Error
        nodeId: string
      }

      const errorsList: ErrorList[] = []

      // Iterate through all the nodeIds, and resolve the promise once all the requests have been fulfilled
      nodeIds.forEach((nodeId) => {
        // add nodeId to the config, to get it again on then() when the promise is resolved
        promises.push(
          deepBoxAPI.value.nodesMove
            .create(nodeId, params)
            .then((res) => {
              handleApiResponse(nodeId)
              return res
            })
            .catch((e) => {
              errorsList.push({ nodeId, e })
              throw e
            }),
        )
      })

      await Promise.all(promises)

      // handle rejected promises
      const errorsMoveRecursive: unknown[] = []
      const errorsMoveProhibitedGenericFolder: unknown[] = []
      const errorsMoveProhibitedSharedFolder: unknown[] = []
      const errorsMoveOther: unknown[] = []
      errorsList.forEach(({ e }) => {
        let statusCode: number | undefined
        if (isHttpError(e)) {
          statusCode = e.response.status

          const errorData = e.response?.data as GenericError
          const errorDataMessageId = errorData?.messageId

          if (statusCode === 422) {
            switch (errorDataMessageId) {
              case DEEP_BOX_API_ERROR_MSGS.NODE_MOVE_OUT_OF_BOX_PROHIBITED_GENERIC_FOLDER:
                errorsMoveProhibitedGenericFolder.push(e)
                break

              case DEEP_BOX_API_ERROR_MSGS.NODE_MOVE_OUT_OF_BOX_PROHIBITED_SHARED_FOLDER:
                errorsMoveProhibitedSharedFolder.push(e)
                break
            }
          } else if (
            errorData &&
            errorDataMessageId === DEEP_BOX_API_ERROR_MSGS.ERROR_MOVE_RECURSIVE
          ) {
            errorsMoveRecursive.push(e)
          } else {
            errorsMoveOther.push(e)
          }
        }
      })

      if (errorsMoveRecursive.length > 0) {
        toast.error(
          t(
            'api.deepbox.errors.node.move.recursive',
            errorsMoveRecursive.length,
          ),
        )
      }

      if (errorsMoveProhibitedGenericFolder.length > 0) {
        toast.error(
          t(
            'api.deepbox.errors.node.move.out-of-box.prohibited.generic-folder',
            errorsMoveProhibitedGenericFolder.length,
          ),
        )
      }

      if (errorsMoveProhibitedSharedFolder.length > 0) {
        toast.error(
          t(
            'api.deepbox.errors.node.move.out-of-box.prohibited.shared-folder',
            errorsMoveProhibitedSharedFolder.length,
          ),
        )
      }

      if (errorsMoveOther.length > 0) {
        toast.error(t('error.error_occurred'))
      }

      // if all the nodes have been processed, resolve the global promise
      return Promise.resolve(successMovedNodesCount)
    } finally {
      moveNodePending.value = true
    }
  }

  async function refreshCurrentNodesOnMove(
    targetId: string,
    sourceNodeIds: string[],
  ) {
    for (const sourceNodeId of sourceNodeIds) {
      const sourceNodeIdExists =
        deepBoxDeepBoxesBoxesNodesStore.data?.nodes?.find(
          (n) => n.nodeId === sourceNodeId,
        )
      if (sourceNodeIdExists) {
        // remove node from current view
        deepBoxDeepBoxesBoxesNodesStore.removeNodeState(sourceNodeId)
      } else {
        // fetch node and add it to the current view if targetId === current view node id
        if (targetId === deepBoxDeepBoxesBoxesNodesStore.getCurrentRootNodeId) {
          const { data } = await deepBoxAPI.value.nodesInfo.get(sourceNodeId)
          if (data) {
            deepBoxDeepBoxesBoxesNodesStore.addNodeState(data.node)
          }
        }
      }
    }
  }

  return {
    moveNodePending,
    moveNodes,
    refreshCurrentNodesOnMove,
  }
}
